import React, { PureComponent } from 'react';
import { add as addEvent, remove as removeEvent } from 'bianco.events';

import Breadcrumbs from 'components/breadcrumbs';
import HeaderCart from './header-cart';
import Icon from 'components/icon';
import { Link } from 'react-router-dom';
import PreHeader from './pre-header';
import PropTypes from 'prop-types';
import Search from './search';
import SidePanelNav from 'components/side-panel-nav';
import classnames from 'classnames';
import { connect } from 'react-redux';
import reduxUtils from 'util/redux';
import staticPath from 'util/misc/static-assets-path';
import linkToHome from 'util/misc/link-to-home';
import PageLoaderBar from 'components/page-loader-bar';
import OfflineNotification from 'components/offline-notification';
import { t } from 'translations';
import CategoryNavigationButton from 'components/category-navigation-button';

function Logo({ isMobile, togglePanel, isMenuOpen, userRegion }) {
    const homeLink = linkToHome(userRegion);
    if (isMobile) {
        return (
            <div className="main-header--logo-inner">
                <button
                    aria-expanded={ isMenuOpen }
                    aria-haspopup="true"
                    aria-label={ t('accessibility.label.navigation_menu') }
                    aria-controls="menu"
                    onClick={ () => togglePanel({ facet: 'menu', content: <SidePanelNav/> }) }
                    className={ classnames('main-header--menu-button', { 'main-header--menu-button__highlighted': isMenuOpen }) }
                >
                    <Icon className="main-header--hamburger" size="medium" symbol={ isMenuOpen ? 'cancel' : 'burger' }/>
                </button>
                <Link to={ homeLink } className="main-header--logo-image">
                    <img className="main-header--logo-img" src={ staticPath('img/logos/amigos-small.svg') }
                        alt={ t('amigos.title') }/>
                </Link>
            </div>
        );
    }

    return (
        <div className="main-header--logo-inner">
            <Link to={ homeLink } className="main-header--logo-image">
                <img className="main-header--logo-img" src={ staticPath('img/logos/amigos-large.svg') }
                    alt={ t('amigos.title') }/>
            </Link>
        </div>
    );
}

Logo.propTypes = {
    isMenuOpen: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool.isRequired,
    userRegion: PropTypes.object,
    togglePanel: PropTypes.func.isRequired,
};

class MainHeader extends PureComponent {
    static propTypes = {
        pathname: PropTypes.string.isRequired,
        isMenuOpen: PropTypes.bool.isRequired,
        isScrollLocked: PropTypes.bool.isRequired,
        isMobile: PropTypes.bool.isRequired,
        scrollbarWidth: PropTypes.number.isRequired,
        viewportWidth: PropTypes.number.isRequired,
        activeCategory: PropTypes.object,
        headerHeight: PropTypes.number.isRequired,
        panelFacet: PropTypes.string,
        setActiveCategory: PropTypes.func.isRequired,
        setHeaderHeight: PropTypes.func.isRequired,
        togglePanel: PropTypes.func.isRequired,
        isModalOpen: PropTypes.bool.isRequired,
        openPanel: PropTypes.func.isRequired,
        closePanel: PropTypes.func.isRequired,
        userRegion: PropTypes.object,
        routerRegistry: PropTypes.object.isRequired,
        currentRouteName: PropTypes.string.isRequired,
        templateConfig: PropTypes.object.isRequired,
        isLoaderVisible: PropTypes.bool.isRequired,
        breadcrumbs: PropTypes.array.isRequired,
    };

    componentDidMount() {
        if (this.refs.headerLogo) {
            addEvent(this.refs.headerLogo, 'swiperight', this.openPanel);
            addEvent(this.refs.headerLogo, 'swipeleft', this.closePanel);
        }
        requestAnimationFrame(() => {
            this.setHeaderHeight();
        });
    }

    componentDidUpdate({ isMobile, templateConfig }) {
        if (isMobile !== this.props.isMobile || (!isMobile && templateConfig.header !== this.props.templateConfig.header)) {
            requestAnimationFrame(() => {
                this.setHeaderHeight();
            });
        }
    }

    componentWillUnmount() {
        if (this.refs.headerLogo) {
            removeEvent(this.refs.headerLogo, 'swiperight', this.openPanel);
            removeEvent(this.refs.headerLogo, 'swipeleft', this.closePanel);
        }
    }

    setHeaderHeight() {
        if (!this.isNone && this.refs.headerContent) {
            this.props.setHeaderHeight({ height: this.refs.headerContent.offsetHeight });
        }
    }

    closePanel = () => {
        this.props.closePanel();
    };

    get isDefault() {
        return this.props.templateConfig.header === 'default';
    }

    get isMini() {
        return this.props.templateConfig.header === 'mini';
    }

    get isNone() {
        return this.props.templateConfig.header === 'none';
    }

    get isReduced() {
        return this.props.templateConfig.header === 'reduced';
    }

    openPanel = () => {
        this.props.openPanel({ facet: 'menu' });
    };

    renderHeader() {
        const {
            isMobile,
            togglePanel,
            isMenuOpen,
            userRegion,
            panelFacet,
        } = this.props;

        return (
            <div className="main-header--content">
                <div className="main-header--container grid--container grid--container__fluid">
                    <div className="grid--row main-header--row">
                        <div ref="headerLogo" className="grid--col main-header--col main-header--logo">
                            <Logo
                                isMenuOpen={ isMenuOpen && panelFacet === 'menu' }
                                togglePanel={ togglePanel }
                                userRegion={ userRegion }
                                isMobile={ isMobile }/>
                        </div>
                        { this.isReduced || this.isMini ? null :
                            <div className="grid--col main-header--col main-header--search">
                                <Search isReduced={ isMobile }/>
                            </div> }

                        { this.isReduced || this.isMini ? null :
                            <div className="grid--col main-header--col main-header--cart">
                                <div className="main-header--cart-wrapper">
                                    <HeaderCart/>
                                </div>
                            </div> }
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const {
            isMobile,
            setActiveCategory,
            scrollbarWidth,
            isScrollLocked,
            panelFacet,
            isModalOpen,
            headerHeight,
            templateConfig,
            isLoaderVisible,
            breadcrumbs,
        } = this.props;
        const canShowBreadcrumbs = panelFacet !== 'menu';
        const paddingRight = isScrollLocked ? scrollbarWidth : null;
        const hasBreadcrumbs = breadcrumbs.length > 0;

        if (this.isNone) {
            return null;
        }

        return (
            <div ref="root"
                aria-hidden={ isModalOpen }
                className={ classnames(`skel--header main-header grid main-header__${ templateConfig.header }`) }>
                <div className="main-header--placeholder" style={ { height: headerHeight } }/>
                { <PageLoaderBar isActive={ isLoaderVisible }></PageLoaderBar> }
                <OfflineNotification/>
                <div ref="headerContent" className="main-header--content-wrapper grid grid__fluid"
                    style={ canShowBreadcrumbs || this.isMini ? { paddingRight } : {
                        height: headerHeight - 44,
                        overflow: 'hidden',
                        paddingRight,
                    } }>

                    { isMobile || this.isReduced ? null : <PreHeader/> }

                    { (isMobile || !this.isMini) && this.renderHeader() }

                    { isMobile && <CategoryNavigationButton/> }

                    { !this.isMini && <div
                        className={ classnames('main-header--breadcrumbs', {
                            'main-header--breadcrumbs__empty': !hasBreadcrumbs,
                            'main-header--breadcrumbs__visible': canShowBreadcrumbs,
                        }) }>
                        <Breadcrumbs setActiveCategory={ setActiveCategory }/>
                    </div> }
                </div>
            </div>
        );
    }
}

export default connect(
    reduxUtils.mapStateToProps({
        isMobile: 'viewport.isMobile',
        viewportWidth: 'viewport.width',
        headerHeight: 'header.height',
        activeCategory: 'categories.active',
        isModalOpen: 'modal.isVisible',
        isMenuOpen: 'panel.isOpen',
        hasViewportOverlay: 'viewport.hasOverlay',
        panelFacet: 'panel.facet',
        isScrollLocked: 'scroll.isLocked',
        scrollbarWidth: 'scroll.scrollbarWidth',
        pathname: 'router.location.pathname',
        currentRouteName: 'router.name',
        userRegion: 'region.active',
        routerRegistry: 'router.registry',
        templateConfig: 'router.config',
        isLoaderVisible: 'main-content.isLoaderVisible',
        breadcrumbs: 'breadcrumbs.items',
    }),
    reduxUtils.mapDispatchToProps({
        setHeaderHeight: 'header.setHeight',
        setActiveCategory: 'categories.setActiveCategory',
        togglePanel: 'panel.toggle',
        closePanel: 'panel.close',
        openPanel: 'panel.open',
        setBreadcrumbs: 'breadcrumbs.set',
    }),
)(MainHeader);



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/components/main-header/index.js